import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaChevronDown } from "react-icons/fa";
import Logo from "../assets/images/rabta logo.png";
import { getDeviceInfo } from "../utils/deviceInfo";

export default function Retailer() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("register");
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    code: "",
    retailerCode: "",
    privacyPolicy: false,
  });
  const [error, setError] = useState("");
  const [successData, setSuccessData] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState(null);

  // OTP step management
  const [step, setStep] = useState(1); // 1=register/login, 2=OTP
  const [otp, setOtp] = useState("");
  const [otpTimer, setOtpTimer] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [canResendOtp, setCanResendOtp] = useState(true);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const info = await getDeviceInfo();
        setDeviceInfo(info);
      } catch (error) {
        console.error("Failed to get device info:", error);
      }
    };
    fetchInfo();
  }, []);

  useEffect(() => {
    if (location.pathname === "/retailers/login") {
      setActiveTab("login");
    } else {
      setActiveTab("register");
    }
  }, [location.pathname]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData({ ...formData, [name]: checked });
    } else if (name === "name") {
      const filteredValue = value.replace(/[^a-zA-Z\s]/g, "");
      setFormData({ ...formData, [name]: filteredValue });
    } else if (name === "mobile") {
      const filteredValue = value.replace(/[^0-9]/g, "");
      setFormData({ ...formData, [name]: filteredValue });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const validateForm = () => {
    if (activeTab === "register") {
      if (!formData.name.trim()) return t("retailer.validation.fullNameRequired");
      if (!/^[0-9]{10}$/.test(formData.mobile)) return t("retailer.validation.mobileNumberInvalid");
      if (!formData.code.trim()) return t("retailer.validation.tlCodeRequired");
      if (!formData.privacyPolicy) return "Please agree to the Privacy Policy to continue";
    }
    if (activeTab === "login") {
      if (!/^[0-9]{10}$/.test(formData.mobile)) return t("retailer.validation.mobileNumberInvalid");
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setIsSubmitting(true);

    try {
      if (activeTab === "register") {
        const deviceInfoPayload = deviceInfo
          ? {
              device: deviceInfo.device,
              os: deviceInfo.os,
              browser: deviceInfo.browser,
              ipAddress: deviceInfo.ipAddress,
              location: `${deviceInfo.location?.city}, ${deviceInfo.location?.region} ` || null,
              screenSize: deviceInfo.screen
                ? `${deviceInfo.screen?.width}x${deviceInfo.screen?.height}`
                : "",
              timezone: deviceInfo.timezone,
            }
          : {};

        const res = await fetch(`${process.env.REACT_APP_API_URL}retailers/register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fullName: formData.name,
            mobileNumber: formData.mobile,
            tlCode: formData.code,
            deviceInfo: deviceInfoPayload,
          }),
        });

        const data = await res.json();
        if (data.status === "success") {
          setSuccessData(data.data);
          setFormData({ name: "", mobile: "", code: "", retailerCode: "", privacyPolicy: false });
        } else {
          setError(data.message || t("retailer.validation.registrationFailed"));
        }
      } else if (activeTab === "login") {
        // Step 1: Send OTP
        const res = await fetch(`${process.env.REACT_APP_API_URL}retailers/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            mobileNumber: formData.mobile,
          }),
        });

        const data = await res.json();
        if (data.status === "success") {
          setStep(2); // move to OTP screen
          setOtpTimer(60); // start OTP timer
          startOtpTimer();
        } else {
          setError(data.message || t("retailer.validation.loginFailed"));
        }
      }
    } catch (err) {
      console.error("API Error:", err);
      setError(t("retailer.validation.somethingWentWrong"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const startOtpTimer = () => {
    let timeLeft = 60;
    const timer = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanResendOtp(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setError("Please enter a valid 6-digit OTP");
      return;
    }
    setIsSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}retailers/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobileNumber: formData.mobile,
          otp: otp,
        }),
      });
      const data = await res.json();
      if (data.status === "success") {
        localStorage.setItem("retailer", JSON.stringify(data.data));
        setIsOtpVerified(true);
        setTimeout(() => navigate("/retailers/dashboard"), 1000);
      } else {
        setError(data.message || "Invalid OTP");
      }
    } catch (err) {
      console.error("OTP verify error:", err);
      setError("Something went wrong while verifying OTP");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResendOtp) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}retailers/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobileNumber: formData.mobile }),
      });
      const data = await res.json();
      if (data.status === "success") {
        setOtpTimer(60);
        startOtpTimer();
        setCanResendOtp(false);
      }
    } catch (err) {
      console.error("Resend OTP error:", err);
      setError("Failed to resend OTP");
    }
  };

  // ✅ Step 2: OTP Verification Screen
  if (step === 2) {
    return (
      <div className="main-bg">
        <div className="form-container-ret success-screen">
          <img className="logo-main" src={Logo} alt="logo" />
          <div className="otp-block">
            <h2>Verify your number</h2>
            <input
              type="text"
              name="otp"
              placeholder="Enter 6-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
              maxLength="6"
            />

            {otpTimer > 0 && <p>OTP sent successfully to your mobile number</p>}

            {error && <p className="error">{error}</p>}

            {otpTimer > 0 && (
              <button
                onClick={handleVerifyOtp}
                disabled={isSubmitting || isOtpVerified}
                className={isOtpVerified ? "submit-btn verified" : "verifyBtn"}
                style={{ marginTop: "20px" }}
              >
                {isSubmitting ? "Verifying..." : isOtpVerified ? "✓ Verified" : "Confirm"}
              </button>
            )}

            <div className="resend-otp-section">
              {otpTimer > 0 ? (
                <p className="timer-text">
                  Resend OTP in <span className="timer-count">{otpTimer}s</span>
                </p>
              ) : (
                <button
                  className="resend-btn btn-common"
                  onClick={handleResendOtp}
                  disabled={isSubmitting || !canResendOtp}
                >
                  {isSubmitting ? "RESENDING..." : "Resend OTP"}
                </button>
              )}
            </div>

            <button
              style={{ marginTop: "10px" }}
              className="back-btn"
              onClick={() => {
                setStep(1);
                setOtp("");
                setError("");
                setIsOtpVerified(false);
                setOtpTimer(0);
                setCanResendOtp(true);
              }}
              disabled={isSubmitting || isOtpVerified}
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ✅ Step 1: Register/Login screen (default)
  if (successData) {
    return (
      <div className="main-bg">
        <div className="form-container-ret success-screen">
          <img src={Logo} alt="logo" className="logo-main" />
          <h3>{t("retailer.successVerified")}</h3>
          <p>{t("retailer.uniqueRetailerCode")}</p>
          <h1 className="retailer-code">{successData.code || successData.retailerCode}</h1>
          <p className="note">
            {t("retailer.saveRetailerCode")} {t("retailer.codeServesAs")}
          </p>
          <button
            style={{ borderRadius: 25, marginTop: 20 }}
            className="btn-common"
            onClick={() => {
              navigate("/retailers/login");
              setSuccessData(null);
            }}
          >
            {t("retailer.login")}
          </button>
        </div>
      </div>
    );
  }

  // ✅ Default (Step 1)
  return (
    <div className="main-bg">
      <div className="form-container-ret">
        <div className="wrapper-form">
          <div style={{ textAlign: "center" }}>
            <img src={Logo} alt="logo" className="logo-main" />
          </div>

          <div className="tabs">
            <button
              className={activeTab === "register" ? "active" : "test"}
              onClick={() => navigate("/retailers/register")}
            >
              {t("retailer.register")}
            </button>
            <button
              className={activeTab === "login" ? "active" : "test"}
              onClick={() => navigate("/retailers/login")}
            >
              {t("retailer.login")}
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {activeTab === "register" && (
              <>
                <input
                  type="text"
                  name="name"
                  placeholder={t("retailer.fullName") + "*"}
                  value={formData.name}
                  onChange={handleChange}
                />
                <input
                  type="tel"
                  name="mobile"
                  placeholder={t("retailer.mobileNumber") + "*"}
                  value={formData.mobile}
                  onChange={handleChange}
                  maxLength={10}
                />
                <input
                  type="text"
                  name="code"
                  placeholder={t("retailer.tlCode") + "*"}
                  value={formData.code}
                  onChange={handleChange}
                />

                <div className="checkbox-group" style={{ marginTop: "10px" }}>
                  <label className="flex-label">
                    <span className="custom-checkbox-wrapper">
                      <input
                        type="checkbox"
                        name="privacyPolicy"
                        checked={formData?.privacyPolicy || false}
                        onChange={handleChange}
                      />
                      <span className="custom-checkbox"></span>
                    </span>
                    <span className="checkbox-text">
                      By registering, I hereby confirm that I have read and agree to the{" "}
                      <a
                        className="tnc"
                        href="/retailers/privacy-policy"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Privacy Policy
                      </a>
                      .
                    </span>
                  </label>
                </div>

                <button type="submit" className="verifyBtn" disabled={!formData.privacyPolicy}>
                  {t("retailer.verify")}
                </button>

                <div className="footer-line">
                  <div>
                    Got a question or need support?
                    <br /> Reach us at <strong>0120-4420155</strong> between{" "}
                    <strong>10:00 AM and 5:30 PM (Monday–Saturday).</strong>
                  </div>
                  <div className="bottom-footer-line">
                    Contest developed and managed by{" "}
                    <strong>
                      <a
                        href="https://blinkdigital.in/"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "inherit",
                          textDecoration: "none",
                        }}
                      >
                        Blink Digital
                      </a>
                    </strong>
                  </div>
                </div>
              </>
            )}

            {activeTab === "login" && (
              <>
                <input
                  type="tel"
                  name="mobile"
                  placeholder={t("retailer.mobileNumber") + "*"}
                  value={formData.mobile}
                  onChange={handleChange}
                  maxLength={10}
                />
                <button type="submit" className="verifyBtn">
                  {t("retailer.login")}
                </button>
                <div className="footer-line">
                  <div>
                    Got a question or need support?
                    <br /> Reach us at <strong>0120-4420155</strong> between{" "}
                    <strong>10:00 AM and 5:30 PM (Monday–Saturday).</strong>
                  </div>
                  <div className="bottom-footer-line">
                    Contest developed and managed by{" "}
                    <strong>
                      <a
                        href="https://blinkdigital.in/"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "inherit",
                          textDecoration: "none",
                        }}
                      >
                        Blink Digital
                      </a>
                    </strong>
                  </div>
                </div>
              </>
            )}
          </form>

          {error && <p className="error">{error}</p>}
        </div>
      </div>
    </div>
  );
}
